import React, { useState, useEffect } from 'react';
import { useContent } from '../../contexts/ContentContext';
import './SessionsAdmin.css';

const SessionsAdmin = ({ contentPath }) => {
  const { getContent, updateContent } = useContent();
  const [sectionData, setSectionData] = useState(null);

  useEffect(() => {
    const data = getContent(contentPath);
    setSectionData(data);
  }, [contentPath, getContent]);

  const handleSectionUpdate = (field, value) => {
    updateContent(`${contentPath}.${field}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleSessionUpdate = (index, field, value) => {
    updateContent(`${contentPath}.sessions.${index}.${field}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleAddSession = () => {
    const sessions = sectionData?.sessions || [];
    const newSession = {
      title: '',
      instructor: '',
      date: '',
      duration: '',
      location: '',
      badge: '',
      link: ''
    };

    updateContent(`${contentPath}.sessions`, [...sessions, newSession]);
    setSectionData(getContent(contentPath));
  };

  const handleDeleteSession = (index) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette session ?')) {
      const sessions = sectionData?.sessions || [];
      const updatedSessions = sessions.filter((_, i) => i !== index);
      updateContent(`${contentPath}.sessions`, updatedSessions);
      setSectionData(getContent(contentPath));
    }
  };

  const getBadgeColor = (badge) => {
    const badgeColors = {
      'Nouveauté': '#10b981',
      'MasterClass': '#3b82f6',
      'International': '#8b5cf6',
      'Certification': '#f59e0b',
      'Formation Phare': '#ef4444',
      'Méthode Exclusive': '#06b6d4'
    };
    return badgeColors[badge] || '#6b7280';
  };

  if (!sectionData) {
    return (
      <div className="sessions-admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="sessions-admin">
      {/* Section Header Settings */}
      <div className="admin-section-header">
        <div className="section-info">
          <h2>
            <i className="fas fa-calendar-alt"></i>
            Sessions à Venir
          </h2>
          <p>Gérez les sessions de formation qui apparaissent sur votre site web</p>
        </div>
        <button 
          className="btn-add-session"
          onClick={handleAddSession}
        >
          <i className="fas fa-plus"></i>
          Ajouter une session
        </button>
      </div>

      {/* Section Title & Subtitle */}
      <div className="section-settings">
        <div className="setting-group">
          <label>Titre de la section</label>
          <input
            type="text"
            value={sectionData.title || ''}
            onChange={(e) => handleSectionUpdate('title', e.target.value)}
            placeholder="Prochaines Sessions de Formation"
          />
        </div>
        <div className="setting-group">
          <label>Sous-titre</label>
          <textarea
            value={sectionData.subtitle || ''}
            onChange={(e) => handleSectionUpdate('subtitle', e.target.value)}
            placeholder="Description des sessions à venir..."
            rows={2}
          />
        </div>
      </div>

      {/* Sessions List */}
      <div className="sessions-list-admin">
        {(sectionData.sessions || []).map((session, index) => (
          <div key={index} className="session-item-admin">
            <div className="session-header">
              <div className="session-title-area">
                <h3>
                  <i className="fas fa-calendar-alt"></i>
                  Session {index + 1}
                </h3>
                {session.badge && (
                  <span 
                    className="session-badge"
                    style={{
                      background: getBadgeColor(session.badge),
                      color: 'white'
                    }}
                  >
                    {session.badge}
                  </span>
                )}
              </div>
              <div className="session-actions">
                <button
                  className="btn-delete"
                  onClick={() => handleDeleteSession(index)}
                  title="Supprimer cette session"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>

            {/* Clean Horizontal Form Fields */}
            <div className="form-grid">
              <div className="form-group">
                <label>Titre</label>
                <input
                  type="text"
                  value={session.title || ''}
                  onChange={(e) => handleSessionUpdate(index, 'title', e.target.value)}
                  placeholder="Formation Cone Beam CBCT"
                />
              </div>

              <div className="form-group">
                <label>Instructeur</label>
                <input
                  type="text"
                  value={session.instructor || ''}
                  onChange={(e) => handleSessionUpdate(index, 'instructor', e.target.value)}
                  placeholder="Dr. Mourad Ben Maamar"
                />
              </div>

              <div className="form-group">
                <label>Date</label>
                <input
                  type="text"
                  value={session.date || ''}
                  onChange={(e) => handleSessionUpdate(index, 'date', e.target.value)}
                  placeholder="15-17 Mars 2024"
                />
              </div>

              <div className="form-group">
                <label>Durée</label>
                <input
                  type="text"
                  value={session.duration || ''}
                  onChange={(e) => handleSessionUpdate(index, 'duration', e.target.value)}
                  placeholder="3 jours intensifs"
                />
              </div>

              <div className="form-group">
                <label>Localisation</label>
                <input
                  type="text"
                  value={session.location || ''}
                  onChange={(e) => handleSessionUpdate(index, 'location', e.target.value)}
                  placeholder="Alger - Centre de Formation"
                />
              </div>

              <div className="form-group">
                <label>Badge</label>
                <select
                  value={session.badge || ''}
                  onChange={(e) => handleSessionUpdate(index, 'badge', e.target.value)}
                >
                  <option value="">Aucun badge</option>
                  <option value="Nouveauté">Nouveauté</option>
                  <option value="MasterClass">MasterClass</option>
                  <option value="International">International</option>
                  <option value="Certification">Certification</option>
                  <option value="Formation Phare">Formation Phare</option>
                  <option value="Méthode Exclusive">Méthode Exclusive</option>
                </select>
              </div>

              <div className="form-group full-width">
                <label>Lien</label>
                <input
                  type="text"
                  value={session.link || ''}
                  onChange={(e) => handleSessionUpdate(index, 'link', e.target.value)}
                  placeholder="/formations#cone-beam"
                />
              </div>
            </div>
          </div>
        ))}

      </div>

      {/* Empty State */}
      {(!sectionData.sessions || sectionData.sessions.length === 0) && (
        <div className="empty-state">
          <div className="empty-icon">
            <i className="fas fa-calendar-plus"></i>
          </div>
          <h3>Aucune session programmée</h3>
          <p>Commencez par ajouter votre première session de formation.</p>
          <button className="btn-add-first" onClick={handleAddSession}>
            <i className="fas fa-plus"></i>
            Ajouter la première session
          </button>
        </div>
      )}
    </div>
  );
};

export default SessionsAdmin;
